'use client';

import { Separator } from '@/components/ui/separator';
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { Info } from 'lucide-react';
import Price from '@/components/elements/Properties/Price';
import { useMediaQuery } from '@/components/ui/stepper/use-media-query';
import { useState, useEffect, useRef } from 'react';

export interface PriceBreakdownProps {
  currency: string;
  priceData: {
    nights?: number;
    totalStayPrice: number;
    cleaningFee?: number;
    insuranceFee?: number;
    kawkbFee: number;
    paymentFee: number;
    totalPrice: number;
  };
  displayNights?: boolean;
}

type TooltipState = {
  insurance: boolean;
  kawkb: boolean;
  payment: boolean;
};

export default function PriceBreakdown({
  priceData,
  displayNights = true,
  currency,
}: PriceBreakdownProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [openTooltips, setOpenTooltips] = useState<TooltipState>({
    insurance: false,
    kawkb: false,
    payment: false,
  });
  const tooltipRefs = useRef<{ [_key in keyof TooltipState]: HTMLButtonElement | null }>({
    insurance: null,
    kawkb: null,
    payment: null,
  });

  useEffect(() => {
    if (!isMobile) return;

    const handleClickOutside = (event: MouseEvent) => {
      const isClickInsideTooltip = Object.entries(tooltipRefs.current).some(([_key, ref]) => {
        if (ref && ref.contains(event.target as Node)) {
          return true;
        }
        return false;
      });

      if (!isClickInsideTooltip) {
        setOpenTooltips({
          insurance: false,
          kawkb: false,
          payment: false,
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobile]);

  const handleTooltipClick = (tooltipName: keyof TooltipState) => {
    if (isMobile) {
      setOpenTooltips((prev) => ({
        insurance: false,
        kawkb: false,
        payment: false,
        [tooltipName]: !prev[tooltipName],
      }));
    }
  };

  const setTooltipRef = (element: HTMLButtonElement | null, key: keyof TooltipState) => {
    tooltipRefs.current[key] = element;
  };

  return (
    <div className="grid gap-4 w-full">
      {displayNights && (
        <div className="flex items-center justify-between">
          <div className="text-gray-500 dark:text-gray-400">
            {priceData.nights} {priceData.nights === 1 ? 'Night' : 'Nights'}
          </div>
          <div>
            <Price price={priceData.totalStayPrice} currency={currency} />
          </div>
        </div>
      )}

      {(priceData.cleaningFee ?? 0) > 0 && (
        <div className="flex items-center justify-between">
          <div className="text-gray-500 dark:text-gray-400">Cleaning Fee</div>
          <div>
            <Price price={priceData.cleaningFee ?? 0} currency={currency} />
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-gray-500 dark:text-gray-400">
          <TooltipProvider>
            <Tooltip
              open={isMobile ? openTooltips.insurance : undefined}
              delayDuration={isMobile ? 0 : 200}
            >
              <TooltipTrigger asChild onClick={() => handleTooltipClick('insurance')}>
                <button
                  ref={(el) => setTooltipRef(el, 'insurance')}
                  className="flex items-center gap-2 focus:outline-none"
                >
                  Insurance Fee <Info className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side={isMobile ? 'top' : 'right'} className="max-w-[200px] text-sm">
                <p>
                  Insurance Fee is a one-time fee that covers the cost of insurance for the
                  property, once you check out you are will be automatically refunded.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div>
          {(priceData.insuranceFee ?? 0) > 0 ? (
            <Price price={priceData.insuranceFee ?? 0} currency={currency} />
          ) : (
            <span className="text-xs">(free)</span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-gray-500 dark:text-gray-400">
          <TooltipProvider>
            <Tooltip
              open={isMobile ? openTooltips.kawkb : undefined}
              delayDuration={isMobile ? 0 : 200}
            >
              <TooltipTrigger asChild onClick={() => handleTooltipClick('kawkb')}>
                <button
                  ref={(el) => setTooltipRef(el, 'kawkb')}
                  className="flex items-center gap-2 focus:outline-none"
                >
                  Kawkb Fee <Info className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side={isMobile ? 'top' : 'right'} className="max-w-[200px] text-sm">
                <p>Fee for Kawkb to manage your booking</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div>
          <Price price={priceData.kawkbFee} currency={currency} />
        </div>
      </div>

      {/* Subtotal */}
      <div className="flex items-center justify-between">
        <div className="font-semibold">Subtotal</div>
        <div>
          <Price
            price={
              priceData.totalStayPrice +
              (priceData.cleaningFee ?? 0) +
              (priceData.insuranceFee ?? 0) +
              priceData.kawkbFee
            }
            currency={currency}
          />
        </div>
      </div>

      <Separator />

      {/* Payment Processing Fee */}
      <div className="flex items-center justify-between">
        <div className="text-gray-500 dark:text-gray-400">
          <TooltipProvider>
            <Tooltip
              open={isMobile ? openTooltips.payment : undefined}
              delayDuration={isMobile ? 0 : 200}
            >
              <TooltipTrigger asChild onClick={() => handleTooltipClick('payment')}>
                <button
                  ref={(el) => setTooltipRef(el, 'payment')}
                  className="flex items-center gap-2 focus:outline-none"
                >
                  Payment Fee <Info className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side={isMobile ? 'top' : 'right'} className="max-w-[200px] text-sm">
                <p>A 2% payment processing fee is applied to your booking</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        <div>
          <Price price={priceData.paymentFee} currency={currency} />
        </div>
      </div>

      {/* Total */}
      <div className="flex items-center justify-between">
        <div className="font-semibold">Stay Total</div>
        <div>
          <Price price={priceData.totalPrice} currency={currency} />
        </div>
      </div>
    </div>
  );
}
